import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Upload, X, Loader2, Trash2, Camera, Sparkles, Tag, Check, Smartphone } from 'lucide-react';
import { SERIES_CONFIG } from '../../config/menuConfig';
import CameraModal from './CameraModal';
import { convertToWebP, formatBytes } from '../../utils/imageUtils';
import { clearCatalogCache } from '../../search/catalog';

export const POPULAR_BRANDS = [
    'PROOMTB', 'Rockbros', 'Fox', 'Giro', 'FLY', 'Shimano', 'Garmin',
    'Specialized', 'Raymon', 'Montecci', 'ROTTO', 'Moisture',
    'Troy Lee Designs', 'Maxxis', 'Continental', 'Park Tool',
    'Topeak', 'SRAM', 'Oakley', '100%', 'Bell', 'Lezyne', 'Lazer', 'Muc-Off'
];

export const ACCESSORY_CATEGORIES = [
    'Componentes', 'Cascos', 'Luces', 'Puños', 'Guantillas', 'Herramientas y Aceites',
    'Bombas y CO2', 'Gafas', 'Licras y Pantalones', 'Bulticos y Mochilas',
    'Jerseys', 'Relojes GPS', 'Termeras', 'Nutrición',
    'Ciclo Computadoras GPS', 'Protecciones', 'Pedales', 'Candados', 'Sillines', 'Otros'
];

const ProductForm = ({ type }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const tableName = type === 'bikes' ? 'bicicletas' : 'products';

    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [cameraModalOpen, setCameraModalOpen] = useState(false);
    const [webpBadgeInfo, setWebpBadgeInfo] = useState(null);

    const fileInputRef = useRef(null);
    const mobileCameraInputRef = useRef(null);
    const brandInputRef = useRef(null);

    const [brandList, setBrandList] = useState(POPULAR_BRANDS);
    const [dbCategories, setDbCategories] = useState([]);

    // Form State matching your DB schema usage
    const [formData, setFormData] = useState({
        name: '',
        modelo: '',
        description: '',
        price: '', // Accessories
        precio_eur: '', // Bikes
        category_id: null,
        brand: type === 'bikes' ? '' : 'PROOMTB', // Default brand PROOMTB for accessories
        category: '', // Accessory category / Tipo (e.g. Cascos, Luces)
        serie_id: '',
        image_url: '',
        imagenes_urls: [],
        sizes: [], // Array of { size: 'M', price: 1200 }
        colores: [],
        tipos_marco: [],
        specs: {},
        // Individual spec fields for easier form handling
        cuadro_material: '',
        horquilla: '',
        shock: '',
        motor_modelo: '',
        bateria_wh: '',
        charger: '',
        display: '',
        frenos_modelo: '',
        brake_discs: '',
        transmision_modelo: '',
        shifter: '',
        cassette: '',
        chain: '',
        crank_arm: '',
        chainring: '',
        wheelset: '',
        tire_f: '',
        tire_r: '',
        handlebar: '',
        stem: '',
        saddle: '',
        tija_telescopica: '',
        pedals: '',
        peso_kg: ''
    });

    useEffect(() => {
        // Fetch categories and existing brands dynamically from DB
        const loadMetadata = async () => {
            try {
                const { data: catData } = await supabase.from('categories').select('id, name, slug');
                if (catData) setDbCategories(catData);

                const { data: prodData } = await supabase.from('products').select('brand').not('brand', 'is', null);
                if (prodData) {
                    const uniqueDbBrands = Array.from(new Set(prodData.map(p => p.brand?.trim()).filter(Boolean)));
                    setBrandList(prev => Array.from(new Set(['PROOMTB', ...prev, ...uniqueDbBrands])));
                }
            } catch (err) {
                console.warn('Error loading categories/brands metadata:', err);
            }
        };
        loadMetadata();
    }, []);

    useEffect(() => {
        if (isEditMode) {
            fetchProduct();
        }
    }, [id]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from(tableName)
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;
            // Handle Sizes and Prices zipping for Bikes
            let mappedSizes = [];
            if (type === 'bikes') {
                const dbSizes = Array.isArray(data.sizes) ? data.sizes : [];
                const dbPrices = Array.isArray(data.precio_eur) ? data.precio_eur : [];

                // Zip parallel arrays
                mappedSizes = dbSizes.map((s, i) => ({
                    size: s || '',
                    price: dbPrices[i] || ''
                }));
            } else {
                // Logic for accessories (objects or arrays of objects)
                let rawSizes = data.sizes || [];
                if (typeof rawSizes === 'string') {
                    try { rawSizes = JSON.parse(rawSizes); } catch (e) { rawSizes = []; }
                }
                if (!Array.isArray(rawSizes)) rawSizes = [];
                mappedSizes = rawSizes.map(s => ({
                    size: s.size || s.talla || s.Size || '',
                    price: s.price ?? s.precio ?? s.precio_eur ?? s.Price ?? ''
                }));
            }

            // Get global price (normalize to string/number from potential array)
            const rawGlobalPrice = type === 'bikes' ? data.precio_eur : data.price;
            const dbPriceValue = Array.isArray(rawGlobalPrice) ? rawGlobalPrice[0] : rawGlobalPrice;
            const finalGlobalPrice = dbPriceValue || (mappedSizes.length > 0 ? mappedSizes[0].price : '');

            setFormData({
                ...data,
                name: data.name || data.modelo || '',
                brand: data.brand || (type === 'bikes' ? '' : 'PROOMTB'),
                category: data.category || '',
                category_id: data.category_id || null,
                precio_eur: type === 'bikes' ? finalGlobalPrice : data.precio_eur,
                price: type !== 'bikes' ? finalGlobalPrice : data.price,
                // Ensure arrays/objects are initialized if null
                imagenes_urls: data.imagenes_urls || [],
                sizes: mappedSizes,
                colores: data.colores || [],
                tipos_marco: data.tipos_marco || [],
                specs: data.specs || {},
                // Map flat fields from DB
                cuadro_material: data.cuadro_material || '',
                horquilla: data.horquilla || '',
                shock: data.shock || '',
                motor_modelo: data.motor_modelo || '',
                bateria_wh: data.bateria_wh || '',
                charger: data.charger || '',
                display: data.display || '',
                frenos_modelo: data.frenos_modelo || '',
                brake_discs: data.brake_discs || '',
                transmision_modelo: data.transmision_modelo || '',
                shifter: data.shifter || '',
                cassette: data.cassette || '',
                chain: data.chain || '',
                crank_arm: data.crank_arm || '',
                chainring: data.chainring || '',
                wheelset: data.wheelset || '',
                tire_f: data.tire_f || '',
                tire_r: data.tire_r || '',
                handlebar: data.handlebar || '',
                stem: data.stem || '',
                saddle: data.saddle || '',
                tija_telescopica: data.tija_telescopica || '',
                pedals: data.pedals || '',
                peso_kg: data.peso_kg || ''
            });
        } catch (error) {
            console.error('Error fetching product:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isEditMode && id) {
            fetchProduct();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditMode, tableName]);

    // Sync Global Price with the first variant's price
    useEffect(() => {
        const globalPriceKey = type === 'bikes' ? 'precio_eur' : 'price';
        if (formData.sizes?.length > 0) {
            const firstVariantPrice = formData.sizes[0].price;

            if (firstVariantPrice && firstVariantPrice !== formData[globalPriceKey]) {
                setFormData(prev => ({
                    ...prev,
                    [globalPriceKey]: firstVariantPrice
                }));
            }
        }
    }, [formData.sizes, type, formData.precio_eur, formData.price]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };

            // Auto-set category_id if serie_id changes for bikes
            if (name === 'serie_id' && type === 'bikes') {
                // Find the series key based on the selected ID (value)
                // NOTE: SERIES_CONFIG keys are the IDs. simpler lookup.
                // Wait, in the dropdown mapped value is the KEY of SERIES_CONFIG? 
                // Previous code: value={Object.keys(SERIES_CONFIG).find(key => SERIES_CONFIG[key].id === series.id)}
                // Wait, SERIES_CONFIG keys are integers '1', '2'. SERIES_CONFIG[1].id is 'ravor'.
                // The option value in previous step was complex. Let's simplifiy/verify the option value.
                // If the option value is the KEY (e.g. '1', '2'), we can look up directly.

                const seriesConfig = SERIES_CONFIG[value];
                if (seriesConfig) {
                    newData.category_id = seriesConfig.categoryId;
                }
            }
            return newData;
        });
    };

    // Upload a WebP File to Supabase Storage
    const uploadWebPFile = async (webpFile, meta = {}) => {
        const cleanName = (webpFile.name || 'foto.webp').replace(/[^a-zA-Z0-9_.-]/g, '_');
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}_${cleanName}`;
        const filePath = `${fileName}`;

        // Para accesorios usamos 'products' directamente (evita problemas de RLS en 'bicicletas').
        // Para bicicletas usamos 'bicicletas' y si falla por RLS, reintentamos con 'products'.
        let targetBucket = type === 'bikes' ? 'bicicletas' : 'products';

        let { error: uploadError } = await supabase.storage
            .from(targetBucket)
            .upload(filePath, webpFile, {
                contentType: 'image/webp',
                cacheControl: '31536000',
                upsert: false
            });

        // Fallback automático si 'bicicletas' falla por RLS
        if (uploadError && targetBucket !== 'products') {
            console.warn(`Error al subir a '${targetBucket}' (posible RLS), intentando en 'products'...`, uploadError);
            targetBucket = 'products';
            const fallbackRes = await supabase.storage
                .from('products')
                .upload(filePath, webpFile, {
                    contentType: 'image/webp',
                    cacheControl: '31536000',
                    upsert: false
                });
            uploadError = fallbackRes.error;
        }

        if (uploadError) {
            console.error('Error detallado de Supabase Storage:', uploadError);
            throw new Error(`Error subiendo imagen (${uploadError.statusCode || uploadError.status || 'RLS'}): ${uploadError.message}`);
        }

        const { data: { publicUrl } } = supabase.storage
            .from(targetBucket)
            .getPublicUrl(filePath);

        if (type !== 'bikes') {
            setFormData(prev => ({ ...prev, image_url: publicUrl }));
            setWebpBadgeInfo({
                size: meta.webpSize || webpFile.size,
                width: meta.width,
                height: meta.height
            });
        } else {
            setFormData(prev => ({
                ...prev,
                imagenes_urls: [...prev.imagenes_urls, publicUrl]
            }));
        }

        return publicUrl;
    };

    // Callback when photo is captured in live CameraModal
    const handlePhotoCapturedFromModal = async (capturedData) => {
        try {
            setUploading(true);
            await uploadWebPFile(capturedData.file, capturedData);
        } catch (error) {
            console.error('Error al subir foto de la cámara:', error);
            alert('Error al guardar la foto: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    // Handle Image Upload from file picker or direct mobile camera trigger
    const handleImageUpload = async (e) => {
        try {
            setUploading(true);
            const files = Array.from(e.target.files || []);
            if (!files.length) return;

            for (const file of files) {
                // Convert each image automatically to WebP format
                const webpResult = await convertToWebP(file, {
                    maxWidth: 1600,
                    maxHeight: 1600,
                    quality: 0.85,
                    fallbackName: 'accesorio.webp'
                });

                await uploadWebPFile(webpResult.file, webpResult);
            }
        } catch (error) {
            console.error('Error uploading/converting image:', error);
            alert('Error al procesar y subir la imagen: ' + error.message);
        } finally {
            setUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const removeImage = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            imagenes_urls: prev.imagenes_urls.filter((_, index) => index !== indexToRemove)
        }));
    };

    const addVariant = () => {
        setFormData(prev => ({
            ...prev,
            sizes: [...(prev.sizes || []), { size: '', price: '' }]
        }));
    };

    const updateVariant = (index, field, value) => {
        setFormData(prev => {
            const newSizes = [...prev.sizes];
            newSizes[index] = { ...newSizes[index], [field]: value };
            return { ...prev, sizes: newSizes };
        });
    };

    const removeVariant = (index) => {
        setFormData(prev => ({
            ...prev,
            sizes: prev.sizes.filter((_, i) => i !== index)
        }));
    };

    // Generic Array Handlers for Colors and Frames
    const addArrayItem = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: [...(prev[field] || []), '']
        }));
    };

    const updateArrayItem = (field, index, value) => {
        setFormData(prev => {
            const newArray = [...prev[field]];
            newArray[index] = value;
            return { ...prev, [field]: newArray };
        });
    };

    const removeArrayItem = (field, index) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            // Construct payload based on table columns
            let payload = {};

            if (type === 'bikes') {
                // Table: bicicletas
                payload = {
                    modelo: formData.name || formData.modelo,
                    description: formData.description,
                    image_url: formData.imagenes_urls[0] || null, // Primary legacy field
                    imagenes_urls: formData.imagenes_urls,
                    // Unzip parallel arrays for Bikes
                    sizes: formData.sizes.map(v => v.size),
                    precio_eur: formData.sizes.map(v => v.price),
                    serie_id: formData.serie_id ? parseInt(formData.serie_id) : null,
                    colores: formData.colores,
                    tipos_marco: formData.tipos_marco,
                    // Flat fields for DB
                    cuadro_material: formData.cuadro_material,
                    horquilla: formData.horquilla,
                    shock: formData.shock,
                    motor_modelo: formData.motor_modelo,
                    bateria_wh: formData.bateria_wh ? parseInt(formData.bateria_wh) : null,
                    charger: formData.charger,
                    display: formData.display,
                    frenos_modelo: formData.frenos_modelo,
                    brake_discs: formData.brake_discs,
                    transmision_modelo: formData.transmision_modelo,
                    shifter: formData.shifter,
                    cassette: formData.cassette,
                    chain: formData.chain,
                    crank_arm: formData.crank_arm,
                    chainring: formData.chainring,
                    wheelset: formData.wheelset,
                    tire_f: formData.tire_f,
                    tire_r: formData.tire_r,
                    handlebar: formData.handlebar,
                    stem: formData.stem,
                    saddle: formData.saddle,
                    tija_telescopica: formData.tija_telescopica,
                    pedals: formData.pedals,
                    peso_kg: formData.peso_kg ? parseFloat(formData.peso_kg) : null,
                    specs: formData.specs // Keep original specs object if still used
                    // NOTE: no category_id in bicicletas table
                };
            } else {
                // Table: products
                // Validar UUID para category_id o buscar coincidencia en dbCategories
                const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                let finalCategoryId = null;

                if (formData.category) {
                    const cleanCat = formData.category.trim().toLowerCase();
                    const matched = dbCategories.find(c =>
                        (c.name && c.name.trim().toLowerCase() === cleanCat) ||
                        (c.slug && c.slug.trim().toLowerCase() === cleanCat)
                    );
                    if (matched) {
                        finalCategoryId = matched.id;
                    }
                }

                if (!finalCategoryId && formData.category_id && uuidRegex.test(formData.category_id)) {
                    finalCategoryId = formData.category_id;
                }

                const finalBrand = formData.brand && formData.brand.trim() ? formData.brand.trim() : 'PROOMTB';

                payload = {
                    name: formData.name,
                    description: formData.description,
                    price: parseFloat(formData.price) || 0,
                    category_id: finalCategoryId,
                    category: formData.category ? formData.category.trim() : 'Accesorios',
                    brand: finalBrand,
                    image_url: formData.image_url,
                    sizes: (formData.sizes || []).map(s => ({
                        size: s.size,
                        price: parseFloat(s.price) || 0
                    }))
                };
            }

            let error;
            if (isEditMode) {
                const { error: updateError } = await supabase
                    .from(tableName)
                    .update(payload)
                    .eq('id', id);
                error = updateError;
            } else {
                const { error: insertError } = await supabase
                    .from(tableName)
                    .insert([payload]);
                error = insertError;
            }

            if (error) throw error;

            clearCatalogCache();
            alert(isEditMode ? '¡Producto actualizado con éxito!' : '¡Producto agregado con éxito!');
            navigate(type === 'bikes' ? '/admin/bikes' : '/admin/accessories');
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Error saving product: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEditMode && (!formData.name && !formData.modelo)) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="max-w-4xl mx-auto pb-20">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate(type === 'bikes' ? '/admin/bikes' : '/admin/accessories')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-2xl font-black uppercase tracking-tight">
                    {isEditMode ? 'Edit Product' : 'New Product'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Info Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
                    <h2 className="text-lg font-bold uppercase tracking-wider text-black border-b border-gray-50 pb-4">Basic Information</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase text-gray-400">Name / Model</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name || ''}
                                onChange={handleChange}
                                className="w-full p-3 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-black transition-all"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase text-gray-400">
                                Global Price ({type === 'bikes' ? 'USD' : 'RD$'})
                            </label>
                            <input
                                type="number"
                                name={type === 'bikes' ? 'precio_eur' : 'price'}
                                step="0.01"
                                value={(type === 'bikes' ? formData.precio_eur : formData.price) || ''}
                                onChange={handleChange}
                                className="w-full p-3 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-black transition-all"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {type === 'bikes' ? (
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase text-gray-400">Series</label>
                                <select
                                    name="serie_id"
                                    value={formData.serie_id || ''}
                                    onChange={handleChange}
                                    className="w-full p-3 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-black transition-all"
                                >
                                    <option value="">Select Series</option>
                                    {Object.entries(SERIES_CONFIG).map(([key, series]) => (
                                        <option key={key} value={key}>
                                            {series.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase text-gray-400">Sección</label>
                                <div className="p-3 bg-gray-50 rounded-xl text-sm font-semibold text-gray-700 flex items-center justify-between">
                                    <span>Accesorios y Componentes</span>
                                    <span className="text-[10px] font-black uppercase px-2 py-0.5 bg-gray-200 text-gray-700 rounded-md">
                                        Catálogo
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase text-gray-400">Weight (kg)</label>
                            <input
                                type="number"
                                name="peso_kg"
                                step="0.1"
                                value={formData.peso_kg || ''}
                                onChange={handleChange}
                                className="w-full p-3 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-black transition-all"
                                placeholder="e.g. 24.5"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-bold uppercase text-gray-400">Description</label>
                        <textarea
                            name="description"
                            rows="4"
                            value={formData.description || ''}
                            onChange={handleChange}
                            className="w-full p-3 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-black resize-none transition-all"
                        />
                    </div>
                </div>

                {/* Brand & Type Section for Accessories */}
                {type !== 'bikes' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
                        <div className="flex items-center justify-between border-b border-gray-50 pb-4">
                            <div>
                                <h2 className="text-lg font-bold uppercase tracking-wider text-black flex items-center gap-2">
                                    <Tag size={20} className="text-red-600" />
                                    Marca y Tipo de Accesorio
                                </h2>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    Define la marca del accesorio y el tipo para facilitar la búsqueda en tienda y filtros
                                </p>
                            </div>
                            <span className="text-[10px] font-black uppercase px-2.5 py-1 bg-red-50 text-red-600 rounded-full border border-red-100">
                                Clasificación
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Marca */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold uppercase text-gray-700">Marca del Accesorio</label>
                                    {formData.brand && (
                                        <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md flex items-center gap-1">
                                            <Check size={12} />
                                            {formData.brand}
                                        </span>
                                    )}
                                </div>
                                <div className="relative">
                                    <input
                                        ref={brandInputRef}
                                        type="text"
                                        name="brand"
                                        list="popular-brands-list"
                                        value={formData.brand || ''}
                                        onChange={handleChange}
                                        placeholder="Escribe cualquier marca (Default: PROOMTB)"
                                        className="w-full p-3.5 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-black transition-all text-sm font-semibold"
                                    />
                                    {formData.brand && formData.brand.trim().toUpperCase() !== 'PROOMTB' && (
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, brand: 'PROOMTB' }))}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase px-2 py-1 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded transition-colors"
                                            title="Restablecer marca a PROOMTB"
                                        >
                                            Reset PROOMTB
                                        </button>
                                    )}
                                </div>
                                <datalist id="popular-brands-list">
                                    {brandList.map(b => (
                                        <option key={b} value={b} />
                                    ))}
                                </datalist>

                                <div className="space-y-1.5 pt-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] uppercase font-bold text-gray-400">Marcas sugeridas:</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (brandInputRef.current) {
                                                    brandInputRef.current.focus();
                                                    brandInputRef.current.select();
                                                }
                                            }}
                                            className="text-[10px] font-bold text-red-600 hover:text-red-700 transition-colors"
                                        >
                                            + Escribir otra marca
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1">
                                        {/* Marca personalizada si el usuario escribió una no listada */}
                                        {formData.brand && !brandList.some(b => b.toLowerCase() === formData.brand.trim().toLowerCase()) && (
                                            <button
                                                type="button"
                                                onClick={() => { }}
                                                className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-tight bg-black text-white shadow-sm ring-2 ring-black/20 flex items-center gap-1"
                                            >
                                                <Check size={12} />
                                                <span>{formData.brand}</span>
                                                <span className="text-[9px] opacity-75 font-normal">(Personalizada)</span>
                                            </button>
                                        )}

                                        {brandList.map((brandName) => {
                                            const isSelected = (formData.brand || '').trim().toLowerCase() === brandName.toLowerCase();
                                            const isProomtb = brandName.toUpperCase() === 'PROOMTB';
                                            return (
                                                <button
                                                    key={brandName}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, brand: brandName }))}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-tight transition-all flex items-center gap-1 ${isSelected
                                                            ? isProomtb
                                                                ? 'bg-red-600 text-white shadow-sm ring-2 ring-red-600/30'
                                                                : 'bg-black text-white shadow-sm ring-2 ring-black/20'
                                                            : isProomtb
                                                                ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 font-extrabold'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    {isProomtb && <Sparkles size={11} className={isSelected ? 'text-white' : 'text-red-500'} />}
                                                    {brandName}
                                                    {isProomtb && <span className="text-[9px] opacity-80">(Default)</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Tipo */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold uppercase text-gray-700">Tipo de Accesorio</label>
                                    {formData.category && (
                                        <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                                            {formData.category}
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="text"
                                    name="category"
                                    list="accessory-categories-list"
                                    value={formData.category || ''}
                                    onChange={handleChange}
                                    placeholder="Escribe o selecciona un tipo (ej. Cascos, Luces, Puños...)"
                                    className="w-full p-3.5 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-black transition-all text-sm font-semibold"
                                />
                                <datalist id="accessory-categories-list">
                                    {ACCESSORY_CATEGORIES.map(cat => (
                                        <option key={cat} value={cat} />
                                    ))}
                                </datalist>

                                <div className="space-y-1.5 pt-1">
                                    <span className="text-[10px] uppercase font-bold text-gray-400">Tipos populares:</span>
                                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                                        {ACCESSORY_CATEGORIES.map((catName) => {
                                            const isSelected = (formData.category || '').trim().toLowerCase() === catName.toLowerCase();
                                            return (
                                                <button
                                                    key={catName}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, category: catName }))}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-tight transition-all ${isSelected
                                                            ? 'bg-red-600 text-white shadow-sm ring-2 ring-red-600/20'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    {catName}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Variants & Colors Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Sizes & Prices Variants */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
                        <div className="flex items-center justify-between border-b border-gray-50 pb-4">
                            <h2 className="text-lg font-bold uppercase tracking-wider text-black">Sizes & Prices</h2>
                            <button
                                type="button"
                                onClick={addVariant}
                                className="text-[10px] font-black uppercase bg-black text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                + Add Variant
                            </button>
                        </div>

                        <div className="space-y-3">
                            {formData.sizes?.map((variant, idx) => (
                                <div key={idx} className="flex gap-4 items-center bg-gray-50 p-3 rounded-xl">
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            placeholder="Size (S, M, 42...)"
                                            value={variant.size}
                                            onChange={(e) => updateVariant(idx, 'size', e.target.value)}
                                            className="w-full bg-white border-none rounded-lg p-2 text-sm focus:ring-2 focus:ring-black"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            type="number"
                                            placeholder="Price"
                                            value={variant.price}
                                            onChange={(e) => updateVariant(idx, 'price', e.target.value)}
                                            className="w-full bg-white border-none rounded-lg p-2 text-sm focus:ring-2 focus:ring-black"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeVariant(idx)}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                            {(!formData.sizes || formData.sizes.length === 0) && (
                                <div className="text-center py-6 text-gray-400 text-xs italic">
                                    No variants added. Global price will be used.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Colors & Frames */}
                    <div className="space-y-8">
                        {/* Colors */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
                            <div className="flex items-center justify-between border-b border-gray-50 pb-4">
                                <h2 className="text-lg font-bold uppercase tracking-wider text-black">Colors</h2>
                                <button
                                    type="button"
                                    onClick={() => addArrayItem('colores')}
                                    className="text-[10px] font-black uppercase bg-black text-white px-3 py-1.5 rounded-lg hover:bg-gray-800"
                                >
                                    + Add Color
                                </button>
                            </div>
                            <div className="space-y-2">
                                {formData.colores?.map((color, idx) => (
                                    <div key={idx} className="flex gap-2">
                                        <input
                                            type="text"
                                            value={color}
                                            placeholder="Color name..."
                                            onChange={(e) => updateArrayItem('colores', idx, e.target.value)}
                                            className="flex-1 bg-gray-50 border-none rounded-lg p-2 text-sm focus:ring-2 focus:ring-black"
                                        />
                                        <button type="button" onClick={() => removeArrayItem('colores', idx)} className="text-red-400 p-2"><X size={14} /></button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Frames */}
                        {type === 'bikes' && (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
                                <div className="flex items-center justify-between border-b border-gray-50 pb-4">
                                    <h2 className="text-lg font-bold uppercase tracking-wider text-black">Frame Types</h2>
                                    <button
                                        type="button"
                                        onClick={() => addArrayItem('tipos_marco')}
                                        className="text-[10px] font-black uppercase bg-black text-white px-3 py-1.5 rounded-lg hover:bg-gray-800"
                                    >
                                        + Add Frame
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {formData.tipos_marco?.map((frame, idx) => (
                                        <div key={idx} className="flex gap-2">
                                            <input
                                                type="text"
                                                value={frame}
                                                placeholder="Frame type..."
                                                onChange={(e) => updateArrayItem('tipos_marco', idx, e.target.value)}
                                                className="flex-1 bg-gray-50 border-none rounded-lg p-2 text-sm focus:ring-2 focus:ring-black"
                                            />
                                            <button type="button" onClick={() => removeArrayItem('tipos_marco', idx)} className="text-red-400 p-2"><X size={14} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Technical Specifications Section (Bikes Only) */}
                {type === 'bikes' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-8">
                        <h2 className="text-lg font-bold uppercase tracking-wider text-black border-b border-gray-50 pb-4">Technical Specifications</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                            {[
                                { label: 'Frame Material', name: 'cuadro_material' },
                                { label: 'Fork (Horquilla)', name: 'horquilla' },
                                { label: 'Shock', name: 'shock' },
                                { label: 'Motor', name: 'motor_modelo' },
                                { label: 'Battery (Wh)', name: 'bateria_wh', type: 'number' },
                                { label: 'Charger', name: 'charger' },
                                { label: 'Display', name: 'display' },
                                { label: 'Brakes', name: 'frenos_modelo' },
                                { label: 'Brake Discs', name: 'brake_discs' },
                                { label: 'Rear Derailleur', name: 'transmision_modelo' },
                                { label: 'Shifter', name: 'shifter' },
                                { label: 'Cassette', name: 'cassette' },
                                { label: 'Chain', name: 'chain' },
                                { label: 'Crank Arm', name: 'crank_arm' },
                                { label: 'Chainring', name: 'chainring' },
                                { label: 'Wheelset', name: 'wheelset' },
                                { label: 'Tire (Front)', name: 'tire_f' },
                                { label: 'Tire (Rear)', name: 'tire_r' },
                                { label: 'Handlebar', name: 'handlebar' },
                                { label: 'Stem', name: 'stem' },
                                { label: 'Saddle', name: 'saddle' },
                                { label: 'Dropper Post', name: 'tija_telescopica' },
                                { label: 'Pedals', name: 'pedals' },
                            ].map((spec) => (
                                <div key={spec.name} className="space-y-1">
                                    <label className="text-[10px] font-black uppercase text-gray-400">{spec.label}</label>
                                    <input
                                        type={spec.type || "text"}
                                        name={spec.name}
                                        value={formData[spec.name] || ''}
                                        onChange={handleChange}
                                        className="w-full p-2.5 bg-gray-50 rounded-xl border-none text-sm focus:ring-2 focus:ring-black transition-all"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Images Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-50 pb-4">
                        <div>
                            <h2 className="text-lg font-bold uppercase tracking-wider text-black flex items-center gap-2">
                                <Camera size={20} className="text-red-600" />
                                {type === 'bikes' ? 'Imágenes del Producto' : 'Foto del Accesorio'}
                            </h2>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {type === 'bikes'
                                    ? 'Múltiples imágenes permitidas. Las fotos se optimizan automáticamente a formato WebP.'
                                    : 'Tira una foto con la cámara o sube un archivo. Se convertirá automáticamente a WebP optimizado.'}
                            </p>
                        </div>
                        <span className="text-[10px] font-black uppercase px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200/50 w-fit flex items-center gap-1">
                            <Sparkles size={12} />
                            Auto-WebP Activo
                        </span>
                    </div>

                    {/* Hidden Native Inputs */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleImageUpload}
                        className="hidden"
                        accept="image/*"
                        disabled={uploading}
                        multiple={type === 'bikes'}
                    />
                    <input
                        ref={mobileCameraInputRef}
                        type="file"
                        onChange={handleImageUpload}
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        disabled={uploading}
                    />

                    {type !== 'bikes' ? (
                        <div className="space-y-6">
                            {formData.image_url ? (
                                <div className="flex flex-col sm:flex-row items-center gap-6 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                    <div className="relative group w-48 h-48 bg-white rounded-xl border border-gray-200 p-3 overflow-hidden shadow-sm flex items-center justify-center">
                                        <img
                                            src={formData.image_url}
                                            alt="Accesorio"
                                            className="w-full h-full object-contain mix-blend-multiply"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, image_url: '' }));
                                                setWebpBadgeInfo(null);
                                            }}
                                            className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white gap-1"
                                        >
                                            <Trash2 size={24} />
                                            <span className="text-[11px] font-bold uppercase tracking-wider">Eliminar Foto</span>
                                        </button>
                                    </div>

                                    <div className="flex-1 space-y-3 text-center sm:text-left">
                                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase bg-emerald-100 text-emerald-800">
                                            <Check size={14} /> Foto WebP Lista
                                        </div>
                                        <h4 className="text-sm font-bold text-gray-900">Foto asignada correctamente</h4>
                                        <p className="text-xs text-gray-500 max-w-md">
                                            La foto está guardada en formato WebP para asegurar velocidad ultra rápida en la tienda y catálogo.
                                        </p>
                                        {webpBadgeInfo && (
                                            <p className="text-[11px] font-mono text-gray-400">
                                                {webpBadgeInfo.width ? `${webpBadgeInfo.width}×${webpBadgeInfo.height} px · ` : ''}
                                                {formatBytes(webpBadgeInfo.size)}
                                            </p>
                                        )}

                                        <div className="flex flex-wrap gap-2 pt-1 justify-center sm:justify-start">
                                            <button
                                                type="button"
                                                onClick={() => setCameraModalOpen(true)}
                                                className="px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
                                            >
                                                <Camera size={14} />
                                                Tirar Otra Foto
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
                                            >
                                                <Upload size={14} />
                                                Cambiar Archivo
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {/* Action 1: Live Camera Viewfinder Modal */}
                                    <button
                                        type="button"
                                        disabled={uploading}
                                        onClick={() => setCameraModalOpen(true)}
                                        className="p-6 bg-red-50/50 hover:bg-red-50 border-2 border-dashed border-red-200 hover:border-red-500 rounded-2xl flex flex-col items-center justify-center text-center transition-all group cursor-pointer"
                                    >
                                        <div className="w-14 h-14 bg-red-600 text-white rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md shadow-red-600/20">
                                            <Camera size={26} />
                                        </div>
                                        <span className="text-xs font-black uppercase tracking-wider text-gray-900 group-hover:text-red-600">
                                            Tirar Foto en Vivo
                                        </span>
                                        <span className="text-[11px] text-gray-500 mt-1">
                                            Abre la cámara web o trasera con visor en pantalla
                                        </span>
                                        <span className="mt-3 px-2.5 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold uppercase">
                                            Cámara Interactiva
                                        </span>
                                    </button>

                                    {/* Action 2: Mobile Direct Shutter */}
                                    <button
                                        type="button"
                                        disabled={uploading}
                                        onClick={() => mobileCameraInputRef.current?.click()}
                                        className="p-6 bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-200 hover:border-black rounded-2xl flex flex-col items-center justify-center text-center transition-all group cursor-pointer"
                                    >
                                        <div className="w-14 h-14 bg-black/5 text-gray-800 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <Smartphone size={26} />
                                        </div>
                                        <span className="text-xs font-black uppercase tracking-wider text-gray-900">
                                            Cámara de Móvil
                                        </span>
                                        <span className="text-[11px] text-gray-500 mt-1">
                                            Disparo directo con la cámara nativa de tu teléfono
                                        </span>
                                        <span className="mt-3 px-2.5 py-0.5 bg-gray-200 text-gray-700 rounded-full text-[10px] font-bold uppercase">
                                            Disparo Rápido
                                        </span>
                                    </button>

                                    {/* Action 3: Upload from Gallery/Computer */}
                                    <button
                                        type="button"
                                        disabled={uploading}
                                        onClick={() => fileInputRef.current?.click()}
                                        className="p-6 bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-200 hover:border-black rounded-2xl flex flex-col items-center justify-center text-center transition-all group cursor-pointer"
                                    >
                                        <div className="w-14 h-14 bg-black/5 text-gray-800 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            {uploading ? <Loader2 className="animate-spin text-gray-600" size={26} /> : <Upload size={26} />}
                                        </div>
                                        <span className="text-xs font-black uppercase tracking-wider text-gray-900">
                                            Subir Archivo
                                        </span>
                                        <span className="text-[11px] text-gray-500 mt-1">
                                            Selecciona cualquier JPG, PNG y se convierte a WebP
                                        </span>
                                        <span className="mt-3 px-2.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-bold uppercase">
                                            Auto WebP
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Bikes multiple image section */
                        <div className="flex flex-wrap gap-4">
                            {formData.imagenes_urls?.map((url, idx) => (
                                <div key={idx} className="relative group w-40 h-40 bg-gray-50 rounded-2xl border border-gray-100 p-2 overflow-hidden">
                                    <img src={url} alt={`View ${idx}`} className="w-full h-full object-contain mix-blend-multiply" />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(idx)}
                                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                                    >
                                        <Trash2 size={24} />
                                    </button>
                                    <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                                        #{idx + 1}
                                    </div>
                                </div>
                            ))}

                            <label className="w-40 h-40 flex flex-col items-center justify-center bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-black hover:bg-gray-100 transition-all">
                                {uploading ? (
                                    <Loader2 className="animate-spin text-gray-400" />
                                ) : (
                                    <>
                                        <div className="w-10 h-10 bg-black/5 rounded-full flex items-center justify-center mb-2">
                                            <Upload size={20} className="text-gray-400" />
                                        </div>
                                        <span className="text-[10px] text-gray-400 font-black uppercase">Subir Imágenes</span>
                                    </>
                                )}
                                <input type="file" onChange={handleImageUpload} className="hidden" accept="image/*" disabled={uploading} multiple />
                            </label>
                        </div>
                    )}
                </div>

                {/* Live Camera Viewfinder Modal */}
                <CameraModal
                    isOpen={cameraModalOpen}
                    onClose={() => setCameraModalOpen(false)}
                    onPhotoCaptured={handlePhotoCapturedFromModal}
                />

                {/* Form Actions */}
                <div className="fixed bottom-8 right-8 z-50">
                    <button
                        type="submit"
                        disabled={loading || uploading}
                        className="bg-black text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-gray-800 transition-all shadow-2xl hover:shadow-black/20 flex items-center gap-3 disabled:opacity-50 active:scale-95"
                    >
                        {loading ? <Loader2 className="animate-spin" size={24} /> : <Save size={24} />}
                        Save Product
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProductForm;
